"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, AtSign, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { AuthInput } from "@/components/auth/AuthInput";
import { createResetClient } from "@/lib/supabase/client";
import { classifyAuthError } from "@/lib/supabase/auth-error-copy";

function validateEmail(email: string): string | null {
  if (!email) return "Enter your email.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return "That doesn't look like a valid email.";
  return null;
}

export default function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Same guard as src/app/login/page.tsx — see the long comment there. Until
  // React hydrates, `onSubmit` is not attached and a click falls through to the
  // native `method="post"`, which nothing handles: the page reloads, the field
  // empties, and `serverError` (React state) is wiped along with it, so the
  // user is told nothing. Enabling the button on hydration removes the only
  // route to that a normal user can take.
  //
  // NOT needed on /auth/update-password or /invite/setup: their submit buttons
  // already gate on `evaluatePassword(password).allMet`, which is false at
  // first render, so those ship disabled in the SSR HTML anyway.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // Detecting hydration is the one case where setting state in an effect IS
    // the pattern — the effect running at all is precisely the signal. The
    // directive has to sit immediately above the call: `disable-next-line`
    // applies to the very next line, so a multi-line reason placed after it
    // disables nothing and earns an "unused directive" warning instead.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHydrated(true);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const fe = validateEmail(email.trim());
    setFieldError(fe);
    if (fe) return;

    setLoading(true);
    const supabase = createResetClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    if (error) {
      const { display } = classifyAuthError(error, "password-reset-request");
      setServerError(display);
      setLoading(false);
      return;
    }
    setSent(true);
    setLoading(false);
  }

  return (
    <div className="mucka-auth-page">
      <div className="mucka-auth-container">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/mucka-orange-logo.png" alt="Mucka" className="mucka-auth-logo" />

        {sent ? (
          <>
            <h1 className="t-display-lg mucka-auth-title">Check your inbox</h1>
            <p className="t-body-md mucka-auth-sub" style={{ marginBottom: 22 }}>
              We&apos;ve sent a reset link to <strong>{email}</strong>. Click it to set a new
              password. If it&apos;s not in your inbox in a minute or two, have a quick check
              in your junk folder.
            </p>

            <div className="mucka-auth-cta">
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                leadingIcon={ArrowLeft}
                onClick={() => (window.location.href = "/login")}
              >
                Back to sign in
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="t-display-lg mucka-auth-title">Forgot password?</h1>
            <p className="t-body-md mucka-auth-sub" style={{ marginBottom: 22 }}>
              Pop in your email and we&apos;ll send you a reset link.
            </p>

            {serverError && (
              <div className="mucka-auth-banner mucka-auth-banner--error">{serverError}</div>
            )}

            {/*
              method="post" as a pre-hydration fallback — see the long note in
              src/app/login/page.tsx. No password here, but an email address in
              a URL is still PII in logs and history.
            */}
            <form className="mucka-auth-form" method="post" onSubmit={handleSubmit} noValidate>
              <AuthInput
                icon={AtSign}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldError}
              />

              <div className="mucka-auth-cta">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  leadingIcon={KeyRound}
                  disabled={loading || !hydrated}
                >
                  {loading ? "Sending…" : "Send Reset Link"}
                </Button>
              </div>
            </form>

            <p className="mucka-auth-foot">
              Remembered it?
              <a href="/login">Back to sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
